/**
 * Client for /api/user/welcome (backend/chainlit/welcome_api.py).
 *
 * One shared store, because two surfaces read the same payload: the card in the
 * empty chat and the "What can I ask?" sheet in the header. A dismissal in one
 * must be visible in the other without a second request.
 */

export interface WelcomeTopic {
  id: string;
  label: string;
  summary: string | null;
  examples: string[];
  updated_through: string | null;
  stale: boolean;
}

export type WelcomeAction =
  | { type: 'topics'; topic?: string }
  | { type: 'reports' }
  | { type: 'renew'; email: string; cc?: string[] };

export interface WelcomeItem {
  id: 'access' | 'data_status' | 'reports' | 'new_topics';
  kind: 'access' | 'data' | 'due' | 'news';
  title: string;
  detail: string;
  fingerprint: string;
  action: WelcomeAction;
}

export interface Welcome {
  mode: 'topics' | 'brief' | 'none';
  since: string | null;
  topics: WelcomeTopic[];
  items: WelcomeItem[];
}

/** Opens the header sheet, optionally scrolled to one topic. */
export const OPEN_TOPIC_GUIDE_EVENT = 'signals-open-topic-guide';
export const openTopicGuide = (topic?: string) =>
  window.dispatchEvent(
    new CustomEvent(OPEN_TOPIC_GUIDE_EVENT, { detail: topic ?? null })
  );

/** Asks the header button to draw the eye once, after the card is closed. */
export const TOPIC_GUIDE_PULSE_EVENT = 'signals-topic-guide-pulse';

/** Opens the scheduled reports dialog that ReportsNav owns. */
export const OPEN_REPORTS_EVENT = 'signals-open-reports';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

let state: Welcome | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getSnapshot = () => state;

/** Re-read from the server. Failures keep what we last had. */
export function refreshWelcome(): Promise<void> {
  if (inflight) return inflight;
  inflight = fetch('/api/user/welcome', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: Welcome | null) => {
      if (d) {
        state = d;
        emit();
      }
    })
    .catch(() => {
      /* the card is optional; never surface an error for it */
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function patch(next: Partial<Welcome>) {
  if (!state) return;
  state = { ...state, ...next };
  emit();
}

/** The topic card was read. Hidden at once; the server records it. */
export function acknowledgeTopics() {
  patch({ mode: 'none' });
  return fetch('/api/user/welcome/ack', {
    method: 'POST',
    credentials: 'include'
  }).catch(() => undefined);
}

/** Dismiss items. Each comes back only when its facts change. */
export function dismissItems(items: WelcomeItem[]) {
  if (!state || items.length === 0) return Promise.resolve(undefined);
  const gone = new Set(items.map((i) => i.id));
  const left = state.items.filter((i) => !gone.has(i.id));
  patch({ items: left, mode: left.length ? state.mode : 'none' });
  return fetch('/api/user/welcome/dismiss', {
    method: 'POST',
    credentials: 'include',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      items: items.map(({ id, fingerprint }) => ({ id, fingerprint }))
    })
  }).catch(() => undefined);
}

/** Topics matching a search, keeping only the examples that match when the
 *  topic itself does not. */
export function filterTopics(
  topics: WelcomeTopic[],
  query: string
): WelcomeTopic[] {
  const q = query.trim().toLowerCase();
  if (!q) return topics;
  const out: WelcomeTopic[] = [];
  for (const t of topics) {
    const own =
      t.label.toLowerCase().includes(q) ||
      (t.summary ?? '').toLowerCase().includes(q);
    const examples = own
      ? t.examples
      : t.examples.filter((e) => e.toLowerCase().includes(q));
    if (own || examples.length) out.push({ ...t, examples });
  }
  return out;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** "27 Sep", the same shape the backend uses in item titles. */
export function dayLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return null;
  // Built by hand: en-GB now prints "Sept", and the backend's titles say "Sep".
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** Test hook: reset the module store between cases. */
export function _resetForTests(next: Welcome | null = null) {
  state = next;
  inflight = null;
}
